import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import type { OwnerSyncStatusListDto, OwnerSyncStatusRowDto, SyncState } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { relative } from '@/lib/utils';
import { visibleInterval } from '@/lib/polling';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { LicenceDeliveryPill } from './LicenceDeliveryPill';
import { ServerStripCard } from './ServerStripCard';
import { useOwnerCapability } from './owner-nav';

const ALL = '';
const AUTO_REFRESH_MS = 60_000;
const PAGE_SIZE = 100;

export const SYNC_STATE_BADGE: Record<SyncState, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' | 'default' }> = {
  needs_attention: { label: 'Needs attention', variant: 'destructive' },
  up_to_date: { label: 'Up to date', variant: 'success' },
  syncing: { label: 'Syncing', variant: 'default' },
  behind: { label: 'Behind', variant: 'warning' },
  offline: { label: 'Offline', variant: 'warning' },
  locked: { label: 'Read-only', variant: 'destructive' },
  not_activated: { label: 'Not activated', variant: 'secondary' },
};

const FACILITY_LABEL: Record<string, string> = { hospital: 'Hospital', lab: 'Laboratory', pharmacy: 'Pharmacy' };

export function OwnerSyncStatusPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [facilityType, setFacilityType] = useState(ALL);
  const [state, setState] = useState(ALL);

  const status = useInfiniteQuery({
    queryKey: ['owner-sync-status', facilityType, state],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      get<OwnerSyncStatusListDto>(`/owner/sync-status${qs({ facilityType: facilityType || undefined, state: state || undefined, limit: PAGE_SIZE, cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: visibleInterval(AUTO_REFRESH_MS),
    placeholderData: (previous) => previous,
  });

  const syncNow = useMutation({
    mutationFn: (tenantId: string) => post(`/owner/tenants/${tenantId}/sync-now`, {}),
    onSuccess: () => {
      toast.success('Sync requested', 'The install picks it up at its next check-in');
      void queryClient.invalidateQueries({ queryKey: ['owner-sync-status'] });
    },
    onError: (error) => toast.error('Could not request a sync', describeError(error)),
  });

  const compare = useMutation({
    mutationFn: (tenantId: string) => post(`/owner/tenants/${tenantId}/sync/compare`, {}),
    onSuccess: () => toast.success('Compare requested', 'The facility server compares itself with the online copy when it is quiet'),
    onError: (error) => toast.error('Could not request a compare', describeError(error)),
  });

  const compareAll = useMutation({
    mutationFn: () => post<{ queued: number }>('/owner/sync/compare-all', {}),
    onSuccess: (result) => toast.success('Compare requested', `${result.queued} facility server(s) will compare themselves with the online copy`),
    onError: (error) => toast.error('Could not request the compares', describeError(error)),
  });

  const rows = status.data?.pages.flatMap((page) => page.items);
  const canSeeStrip = useOwnerCapability('owner.server_strip');

  return (
    <>
      <PageHeader
        title="Offline installs"
        description="Every facility that runs on its own server: whether it is keeping up, how much is waiting to sync, and which owner actions are still on their way to it."
      />
      {canSeeStrip && <ServerStripCard />}
      <SearchFilterBar
        filters={
          <>
            <FilterField label="Type">
              <Select className="w-52" value={facilityType} onChange={(e) => setFacilityType(e.target.value)}>
                <option value={ALL}>All facility types</option>
                {Object.entries(FACILITY_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label="State">
              <Select className="w-52" value={state} onChange={(e) => setState(e.target.value)}>
                <option value={ALL}>Any state</option>
                {(Object.keys(SYNC_STATE_BADGE) as SyncState[]).map((value) => (
                  <option key={value} value={value}>
                    {SYNC_STATE_BADGE[value].label}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        trailing={
          <>
            <Button variant="outline" size="sm" loading={compareAll.isPending} onClick={() => compareAll.mutate()}>
              Check all
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" disabled={status.isFetching} onClick={() => void status.refetch()}>
              <RefreshCw className={status.isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} /> Refresh
            </Button>
          </>
        }
        onReset={
          facilityType || state
            ? () => {
                setFacilityType(ALL);
                setState(ALL);
              }
            : undefined
        }
      />
      <DataTable
        rows={rows}
        loading={status.isLoading} refreshing={status.isFetching}
        error={status.error}
        rowKey={(row: OwnerSyncStatusRowDto) => row.tenantId}
        empty="No facility runs on its own server yet."
        columns={[
          {
            header: 'Facility',
            sort: (row: OwnerSyncStatusRowDto) => row.slug,
            cell: (row: OwnerSyncStatusRowDto) => (
              <Link to={`/owner/tenants/${row.tenantId}`} className="hover:underline">
                <span className="font-mono text-xs">{row.slug}</span>
                <span className="ms-1.5 text-muted-foreground">{row.name}</span>
              </Link>
            ),
          },
          { header: 'Type', sort: (row: OwnerSyncStatusRowDto) => row.facilityType, cell: (row: OwnerSyncStatusRowDto) => FACILITY_LABEL[row.facilityType] ?? row.facilityType },
          {
            header: 'State',
            sort: (row: OwnerSyncStatusRowDto) => row.state,
            cell: (row: OwnerSyncStatusRowDto) => <Badge variant={SYNC_STATE_BADGE[row.state].variant}>{SYNC_STATE_BADGE[row.state].label}</Badge>,
          },
          {
            header: 'Waiting to sync',
            cell: (row: OwnerSyncStatusRowDto) => <WaitingCell row={row} />,
          },
          {
            header: 'Current as of',
            sort: (row: OwnerSyncStatusRowDto) => row.currentAsOf,
            cell: (row: OwnerSyncStatusRowDto) => <span className="text-xs">{row.currentAsOf ? relative(row.currentAsOf) : '—'}</span>,
          },
          {
            header: 'Last check-in',
            sort: (row: OwnerSyncStatusRowDto) => row.lastCheckInAt,
            cell: (row: OwnerSyncStatusRowDto) => (
              <span className="text-xs">{row.state === 'offline' && row.lastCheckInAt ? `Off since ${relative(row.lastCheckInAt)}` : relative(row.lastCheckInAt)}</span>
            ),
          },
          { header: 'Last synced', sort: (row: OwnerSyncStatusRowDto) => row.lastSyncAt, cell: (row: OwnerSyncStatusRowDto) => <span className="text-xs">{relative(row.lastSyncAt)}</span> },
          {
            header: 'Licence',
            cell: (row: OwnerSyncStatusRowDto) => <LicenceDeliveryPill licence={row.licence} lastCheckInAt={row.lastCheckInAt} lockState={row.lockState} />,
          },
          {
            header: 'Server',
            cell: (row: OwnerSyncStatusRowDto) => (
              <span className="text-xs">
                <span className="font-mono">{row.activatedHostname ?? '—'}</span>
                <span className="ms-1.5 text-muted-foreground">{row.runningVersion ?? ''}</span>
                {row.scheduledUpdate && (
                  <span className="mt-0.5 block text-muted-foreground">
                    Update to {row.scheduledUpdate.version} at {new Date(row.scheduledUpdate.at).toLocaleString()}
                    {row.scheduledUpdate.automatic ? ' (required)' : ''}
                  </span>
                )}
              </span>
            ),
          },
          {
            header: 'Owner actions on the way',
            sort: (row: OwnerSyncStatusRowDto) => row.pendingCommands,
            cell: (row: OwnerSyncStatusRowDto) => (row.pendingCommands > 0 ? <Badge variant="warning">{row.pendingCommands}</Badge> : <span className="text-muted-foreground">None</span>),
          },
          {
            header: '',
            cell: (row: OwnerSyncStatusRowDto) => (
              <span className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={syncNow.isPending || row.state === 'not_activated'}
                  onClick={() => syncNow.mutate(row.tenantId)}
                >
                  Sync now
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={compare.isPending || row.state === 'not_activated'}
                  onClick={() => compare.mutate(row.tenantId)}
                >
                  Compare
                </Button>
              </span>
            ),
          },
        ]}
        footer={
          status.hasNextPage && (
            <Button variant="outline" size="sm" loading={status.isFetchingNextPage} onClick={() => void status.fetchNextPage()}>
              Load more
            </Button>
          )
        }
      />
    </>
  );
}

const PARKED_CODE_LABEL: Record<string, string> = {
  FK_MISSING: 'waiting for a linked record',
  RETRY_LIMIT: 'gave up waiting for a linked record',
  UNKNOWN_TABLE: 'needs a newer online service',
  UNKNOWN_COLUMN: 'needs a newer online service',
  CONSTRAINT: 'conflicts with a rule',
  DATA: 'has a value the online copy cannot hold',
  RLS: 'refused by facility separation',
  PAYLOAD: 'arrived empty or too large',
  PARKED: 'not accepted',
};

function bytesLabel(bytes: number | null | undefined): string {
  const value = bytes ?? 0;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${value} B`;
}

function WaitingCell({ row }: { row: OwnerSyncStatusRowDto }) {
  const live = row.backlogLive ?? row.outboxBacklog ?? 0;
  const bulk = row.backlogBulk ?? 0;
  const parked = Object.entries(row.parkedByCode ?? {}).filter(([, count]) => count > 0);
  const differences = row.compareSummary?.differences ?? 0;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {live > 0 ? (
        <span>
          <span className="font-mono">{live}</span>
          {row.oldestPendingAt && <span className="ms-1.5 text-xs text-muted-foreground">oldest {relative(row.oldestPendingAt)}</span>}
        </span>
      ) : (
        <span className="text-muted-foreground">Nothing urgent</span>
      )}
      {bulk > 0 && <Badge variant="secondary">{bulk} log entries</Badge>}
      {(row.filesWaiting ?? 0) > 0 && (
        <Badge variant="secondary">
          {row.filesWaiting} files, {bytesLabel(row.filesWaitingBytes)}
        </Badge>
      )}
      {(row.filesGivenUp ?? 0) > 0 && <Badge variant="destructive">{row.filesGivenUp} files not sent</Badge>}
      {(row.outboxStuck ?? 0) > 0 && <Badge variant="warning">{row.outboxStuck} refused</Badge>}
      {parked.length > 0
        ? parked.map(([code, count]) => (
            <span key={code} title={code}>
              <Badge variant="destructive">
                {count} {PARKED_CODE_LABEL[code] ?? 'not accepted'}
              </Badge>
            </span>
          ))
        : (row.outboxParked ?? 0) > 0 && <Badge variant="destructive">{row.outboxParked} parked</Badge>}
      {row.pausedReason === 'CLOUD_OLDER' && <Badge variant="warning">Paused: online service out of date</Badge>}
      {row.restoreDetectedAt && <Badge variant="destructive">Restored from backup {relative(row.restoreDetectedAt)}</Badge>}
      {differences > 0 && <Badge variant="warning">{differences} differences at last compare</Badge>}
    </span>
  );
}
